/**
 * 瓶頸識別視圖 | Bottleneck Analysis View
* 識別生產瓶頸、資源過載、風險預警
* 注重視覺化與預警提示
 */

import { useState, useMemo } from 'react';
import { useOrderStore, useResourceStore } from '@/store';

type BottleneckType = 'resource' | 'process' | 'material' | 'delay';

interface Bottleneck {
  id: string;
  type: BottleneckType;
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  affectedItems: string[];
  impact: string;
  suggestion: string;
  metrics: {
    label: string;
    value: string | number;
    trend?: 'up' | 'down' | 'stable';
  }[];
}

export function BottleneckAnalysis() {
  const [selectedSeverity, setSelectedSeverity] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [selectedType, setSelectedType] = useState<BottleneckType | 'all'>('all');

  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const persons = useResourceStore((state) => state.persons);
  const machines = useResourceStore((state) => state.machines);
  const materials = useResourceStore((state) => state.materials);

  // 分析瓶頸
  const bottlenecks = useMemo((): Bottleneck[] => {
    const results: Bottleneck[] = [];

    // 1. 資源過載分析
    const personWorkload = new Map<string, number>();
    const machineWorkload = new Map<string, number>();

    subWorkOrders.forEach((order) => {
      if (order.status === '進行中') {
        order.assignedPersons.forEach((personId) => {
          personWorkload.set(personId, (personWorkload.get(personId) || 0) + 1);
        });
        if (order.machineId) {
          machineWorkload.set(order.machineId, (machineWorkload.get(order.machineId) || 0) + 1);
        }
      }
    });

    // 找出高負載人員
    personWorkload.forEach((count, personId) => {
      if (count >= 5) {
        const person = persons.find((p) => p.id === personId);
        if (person) {
          results.push({
            id: `PERSON-${personId}`,
            type: 'resource',
            severity: count >= 7 ? 'high' : 'medium',
            title: `${person.name} 工作過載`,
            description: `${person.name} 目前同時負責 ${count} 個進行中的工單，可能影響產出品質與效率`,
            affectedItems: subWorkOrders
              .filter((o) => o.assignedPersons.includes(personId) && o.status === '進行中')
              .map((o) => o.id),
            impact: '預計延遲 2-3 天',
            suggestion: '建議將部分工單重新分配給其他可用人員，或安排加班處理',
            metrics: [
              { label: '進行中工單', value: count, trend: 'up' },
              { label: '平均工時', value: '10h/天', trend: 'up' },
            ],
          });
        }
      }
    });

    // 找出高負載機台
    machineWorkload.forEach((count, machineId) => {
      if (count >= 4) {
        const machine = machines.find((m) => m.id === machineId);
        if (machine) {
          results.push({
            id: `MACHINE-${machineId}`,
            type: 'resource',
            severity: count >= 6 ? 'high' : 'medium',
            title: `${machine.name} 過度使用`,
            description: `${machine.name} 目前同時處理 ${count} 個工單，可能導致機台過熱或故障風險`,
            affectedItems: subWorkOrders
              .filter((o) => o.machineId === machineId && o.status === '進行中')
              .map((o) => o.id),
            impact: '產能降低約 30%',
            suggestion: '建議安排預防性維護，或將部分工單轉移到備用機台',
            metrics: [
              { label: '進行中工單', value: count, trend: 'up' },
              { label: '使用率', value: `${Math.round((count / 8) * 100)}%`, trend: 'up' },
            ],
          });
        }
      }
    });

    // 2. 物料短缺分析
    materials.forEach((material) => {
      if (material.stockQuantity <= material.safetyStock) {
        const shortageRatio = material.stockQuantity / material.safetyStock;
        results.push({
          id: `MATERIAL-${material.id}`,
          type: 'material',
          severity: shortageRatio <= 0.3 ? 'high' : 'medium',
          title: `${material.name} 庫存不足`,
          description: `目前庫存 ${material.stockQuantity} ${material.unit}，低於安全庫存 ${material.safetyStock} ${material.unit}`,
          affectedItems: [`庫存: ${material.stockQuantity} ${material.unit}`],
          impact: shortageRatio <= 0.3 ? '面臨停線風險' : '可能影響生產進度',
          suggestion: `緊急採購至少 ${material.safetyStock * 2 - material.stockQuantity} ${material.unit} 以補充庫存`,
          metrics: [
            { label: '當前庫存', value: `${material.stockQuantity} ${material.unit}`, trend: 'down' },
            { label: '安全庫存', value: `${material.safetyStock} ${material.unit}`, trend: 'stable' },
            { label: '消耗速度', value: '約 50/天', trend: 'up' },
          ],
        });
      }
    });

    // 3. 延遲風險分析
    subWorkOrders.forEach((order) => {
      if (order.status === '進行中') {
        const remainingDays = Math.ceil((new Date(order.plannedEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        const remainingQuantity = order.plannedQuantity - order.actualQuantity;
        const avgDailyOutput = order.actualQuantity / 10; // 假設已經10天

        if (remainingDays <= 2 && remainingQuantity > avgDailyOutput * remainingDays) {
          results.push({
            id: `DELAY-${order.id}`,
            type: 'delay',
            severity: remainingDays <= 1 ? 'high' : 'medium',
            title: `${order.id} 延遲風險`,
            description: `剩餘 ${remainingDays} 天，但仍有 ${remainingQuantity} 數量未完成，以目前產能計算無法按時完成`,
            affectedItems: [order.id],
            impact: `預計延遲 ${Math.ceil(remainingQuantity / avgDailyOutput - remainingDays)} 天`,
            suggestion: '建議安排加班或增加人手，或與客戶協商延期',
            metrics: [
              { label: '剩餘天數', value: `${remainingDays} 天`, trend: 'down' },
              { label: '剩餘數量', value: remainingQuantity.toLocaleString(), trend: 'stable' },
              { label: '完成率', value: `${Math.round((order.actualQuantity / order.plannedQuantity) * 100)}%`, trend: 'up' },
            ],
          });
        }
      }
    });

    return results.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }, [subWorkOrders, persons, machines, materials]);

  // 過濾瓶頸
  const filteredBottlenecks = useMemo(() => {
    return bottlenecks.filter((b) => {
      if (selectedSeverity !== 'all' && b.severity !== selectedSeverity) return false;
      if (selectedType !== 'all' && b.type !== selectedType) return false;
      return true;
    });
  }, [bottlenecks, selectedSeverity, selectedType]);

  // 統計數據
  const statistics = useMemo(() => {
    return {
      total: bottlenecks.length,
      high: bottlenecks.filter((b) => b.severity === 'high').length,
      medium: bottlenecks.filter((b) => b.severity === 'medium').length,
      low: bottlenecks.filter((b) => b.severity === 'low').length,
      byType: {
        resource: bottlenecks.filter((b) => b.type === 'resource').length,
        process: bottlenecks.filter((b) => b.type === 'process').length,
        material: bottlenecks.filter((b) => b.type === 'material').length,
        delay: bottlenecks.filter((b) => b.type === 'delay').length,
      },
    };
  }, [bottlenecks]);

  // 獲取嚴重度配置
  const getSeverityConfig = (severity: Bottleneck['severity']) => {
    switch (severity) {
      case 'high':
        return {
          bg: 'bg-gradient-to-br from-red-50 to-rose-50',
          border: 'border-red-300',
          text: 'text-red-700',
          icon: '🔴',
          label: '高風險',
        };
      case 'medium':
        return {
          bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
          border: 'border-amber-300',
          text: 'text-amber-700',
          icon: '🟠',
          label: '中風險',
        };
      case 'low':
        return {
          bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
          border: 'border-blue-300',
          text: 'text-blue-700',
          icon: '🔵',
          label: '低風險',
        };
    }
  };

  // 獲取類型配置
  const getTypeConfig = (type: BottleneckType) => {
    switch (type) {
      case 'resource':
        return { icon: '⚠️', label: '資源過載', color: 'text-rose-600' };
      case 'process':
        return { icon: '⚙️', label: '製程問題', color: 'text-amber-600' };
      case 'material':
        return { icon: '📦', label: '物料短缺', color: 'text-orange-600' };
      case 'delay':
        return { icon: '⏰', label: '延遲風險', color: 'text-red-600' };
    }
  };

  return (
    <div className="space-y-4">
      {/* 標題與統計區 */}
      <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
            <span className="text-white text-2xl">🔍</span>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-800">瓶頸識別</h2>
            <p className="text-sm text-slate-600">生產瓶頸、資源過載、風險預警分析</p>
          </div>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* 總瓶頸 */}
          <div className="bg-white/70 border-2 border-red-300 rounded-xl p-4">
            <div className="text-sm font-semibold text-red-700 mb-1">總瓶頸數</div>
            <div className="text-3xl font-black text-red-600">{statistics.total}</div>
          </div>

          {/* 高風險 */}
          <div className="bg-white/70 border-2 border-red-300 rounded-xl p-4">
            <div className="text-sm font-semibold text-red-700 mb-1">高風險</div>
            <div className="text-3xl font-black text-red-600">{statistics.high}</div>
          </div>

          {/* 中風險 */}
          <div className="bg-white/70 border-2 border-amber-300 rounded-xl p-4">
            <div className="text-sm font-semibold text-amber-700 mb-1">中風險</div>
            <div className="text-3xl font-black text-amber-600">{statistics.medium}</div>
          </div>

          {/* 低風險 */}
          <div className="bg-white/70 border-2 border-blue-300 rounded-xl p-4">
            <div className="text-sm font-semibold text-blue-700 mb-1">低風險</div>
            <div className="text-3xl font-black text-blue-600">{statistics.low}</div>
          </div>
        </div>
      </div>

      {/* 過濾器 */}
      <div className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          {/* 嚴重度過濾 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">嚴重度：</label>
            <div className="flex bg-slate-100 rounded-lg overflow-hidden">
              {[
                { value: 'all', label: '全部' },
                { value: 'high', label: '高風險' },
                { value: 'medium', label: '中風險' },
                { value: 'low', label: '低風險' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedSeverity(option.value as typeof selectedSeverity)}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${
                    selectedSeverity === option.value
                      ? 'bg-gradient-to-r from-slate-700 to-slate-600 text-white'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* 類型過濾 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">類型：</label>
            <div className="flex bg-slate-100 rounded-lg overflow-hidden">
              {[
                { value: 'all', label: '全部' },
                { value: 'resource', label: '資源' },
                { value: 'material', label: '物料' },
                { value: 'delay', label: '延遲' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedType(option.value as typeof selectedType)}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${
                    selectedType === option.value
                      ? 'bg-gradient-to-r from-slate-700 to-slate-600 text-white'
                      : 'text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 瓶頸列表 */}
      {filteredBottlenecks.length === 0 ? (
        <div className="bg-white border-2 border-slate-200 rounded-xl p-12 text-center shadow-lg">
          <div className="text-6xl mb-4">✨</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">未發現瓶頸</h3>
          <p className="text-slate-600">目前生產狀況良好，沒有明顯的瓶頸或風險</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBottlenecks.map((bottleneck) => {
            const severityConfig = getSeverityConfig(bottleneck.severity);
            const typeConfig = getTypeConfig(bottleneck.type);

            return (
              <div
                key={bottleneck.id}
                className={`bg-white border-2 ${severityConfig.border} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all`}
              >
                {/* 標題列 */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{typeConfig.icon}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-slate-800">{bottleneck.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${severityConfig.bg} ${severityConfig.text} ${severityConfig.border} border`}>
                          {severityConfig.icon} {severityConfig.label}
                        </span>
                      </div>
                      <p className={`text-sm font-semibold ${typeConfig.color}`}>{typeConfig.label}</p>
                    </div>
                  </div>
                </div>

                {/* 描述 */}
                <p className="text-slate-700 mb-4">{bottleneck.description}</p>

                {/* 影響項目 */}
                {bottleneck.affectedItems.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-slate-600 mb-2">影響項目：</div>
                    <div className="flex flex-wrap gap-2">
                      {bottleneck.affectedItems.map((item, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium border border-slate-300"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 指標 */}
                {bottleneck.metrics.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-slate-600 mb-2">關鍵指標：</div>
                    <div className="grid grid-cols-3 gap-3">
                      {bottleneck.metrics.map((metric, index) => (
                        <div
                          key={index}
                          className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3"
                        >
                          <div className="text-xs text-slate-500 mb-1">{metric.label}</div>
                          <div className="text-lg font-bold text-slate-800">{metric.value}</div>
                          {metric.trend && (
                            <div className="text-xs mt-1">
                              {metric.trend === 'up' && <span className="text-red-600">↗ 上升</span>}
                              {metric.trend === 'down' && <span className="text-green-600">↘ 下降</span>}
                              {metric.trend === 'stable' && <span className="text-slate-600">→ 穩定</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 影響與建議 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 影響 */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm font-bold text-red-700 mb-1">💥 影響</div>
                    <p className="text-sm text-red-800">{bottleneck.impact}</p>
                  </div>

                  {/* 建議 */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-bold text-blue-700 mb-1">💡 建議</div>
                    <p className="text-sm text-blue-800">{bottleneck.suggestion}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
